import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const Button = ({
  variant = "primary",
  size = "md",
  children,
  icon,
  iconPosition = "right",
  to,
  href,
  onClick,
  disabled = false,
  loading = false,
  className = "",
  animate = true,
  type = "button",
  ...props
}) => {
  const variants = {
    primary: "bg-orange-500 text-white hover:bg-orange-600 shadow-lg hover:shadow-xl",
    secondary: "border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white",
    outline: "border-2 border-slate-300 text-slate-700 hover:border-orange-500 hover:text-orange-500",
    ghost: "text-slate-700 hover:text-orange-500 hover:bg-orange-50",
    dark: "bg-slate-900 text-white hover:bg-slate-800",
    white: "bg-white text-slate-900 hover:bg-slate-100"
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg",
    xl: "px-10 py-5 text-xl"
  };

  const baseClasses = `
    inline-flex items-center justify-center space-x-3
    font-semibold rounded-full
    transition-all duration-300
    ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${variants[variant]}
    ${sizes[size]}
    ${animate ? 'transform hover:scale-105' : ''}
    ${className}
  `;

  const content = (
    <>
      {loading && (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {icon && iconPosition === "left" && !loading && (
        <SafeIcon 
          icon={icon} 
          className={`${animate ? 'group-hover:-translate-x-1 transition-transform duration-300' : ''}`} 
        />
      )}
      <span>{children}</span>
      {icon && iconPosition === "right" && !loading && (
        <SafeIcon 
          icon={icon} 
          className={`${animate ? 'group-hover:translate-x-1 transition-transform duration-300' : ''}`} 
        />
      )}
    </>
  );

  const ButtonElement = ({ children: buttonChildren }) => (
    <button
      type={type}
      className={`group ${baseClasses}`}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {buttonChildren}
    </button>
  );

  const LinkElement = ({ children: linkChildren, linkTo }) => (
    <Link
      to={linkTo}
      className={`group ${baseClasses}`}
      {...props}
    >
      {linkChildren}
    </Link>
  );

  const AnchorElement = ({ children: anchorChildren, anchorHref }) => (
    <a
      href={anchorHref}
      className={`group ${baseClasses}`}
      {...props}
    >
      {anchorChildren}
    </a>
  );

  if (to) {
    return <LinkElement linkTo={to}>{content}</LinkElement>;
  }

  if (href) {
    return <AnchorElement anchorHref={href}>{content}</AnchorElement>;
  }

  return <ButtonElement>{content}</ButtonElement>;
};

export default Button;